import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { isEmpty, isEqual } from 'lodash';
import { type ApiAddress, type ApiCustomField, type ApiEnumGroup, type ApiImageWrapper, type ApiOrganization } from '@agunity/api-v4';

import AgButton from '../../../stories/Components/AgButton';
import AgTypography from '../../../stories/Styles/AgTypography';
import AgProfilePage from '../../../stories/Components/PageTemplates/AgProfilePage';
import defaultAvatar from '../../../stories/Components/AgAvatar/defaultAvatar.svg';
import AgToast from '../../../stories/Components/AgToast';
import AgModal from '../../../stories/AgModal';
import AgAvatar from '../../../stories/Components/AgAvatar';
import AgProgressBar from '../../../stories/AgProgressBar';
import type { AgTabsDataProps } from '../../../stories/Components/AgTab';
import OrganizationDetails from '../OrganizationDetails';
import Settings from '../Settings';
import RightSidebar from '../RightSidebar';
import SummaryCardPage from './SummaryCardPage';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getOrganization, getStructure, saveImage, saveNewOrg, selectIsOpenViewAvatar, setFunctionStructureList, setIsDirty_Org_Details, setIsOpenUnsavedModal, setIsOpenViewAvatar, setIsToastOpen_navFromAddOrganization, setOrg_EnumList, setOrg_Redirect_URL, setOrg_TagList } from '../../../redux/slices/organizations';
import { ADDRESS_MAX_LENGTH, CustomFieldItemType, MEMBER_EDIT_PERMISSION, MEMBER_FIELD_NAME_NOTES, MEMBER_PARENT_FIELD_TYPE_NUMBER, ORG_NOTES_MAX_LENGTH, ORG_TYPE_ID, POST_CODE_MAX_LENGTH, ROOT_PATHS, STANDARD_TEXT_MAX_LENGTH } from '../../../Constants';
import { ExtendedRenderTree, selectOrgTree } from '../../../redux/slices/global';
import { findNodeById } from '../../../stories/Utils/TreeUtils';
import { IlicenseFlags } from '../types';
import { getLicenseInfo } from '../../../redux/slices/license';
import { getCustomFieldByTagAndFunction, getEnumList } from '../../Users/redux';

const AddOrganization = () => {
  const dispatch = useAppDispatch();
  const messages = useIntl().formatMessage;
  const navigate = useNavigate();
  
  const functionStructureList = useAppSelector((state) => state.orgs.functionStructureList) || [];
  const orgTree = useAppSelector(selectOrgTree);
  const currentOrgId = useAppSelector((state) => state.orgs.searchOrgId);
  const currentOrg = findNodeById(orgTree as ExtendedRenderTree, currentOrgId as string);
  const org_id = Number(currentOrg?.id);
  const user = useAppSelector(state => state.global.users);
  const permissions = (user?.length && user[0].permissions) || [];
  const editAccess = permissions.includes(MEMBER_EDIT_PERMISSION);

  const selectedRole = useAppSelector(state => state.orgs.selectedRole);

  const isOpenViewAvatar = useAppSelector(selectIsOpenViewAvatar);
  const isOpenUnsavedModal = useAppSelector(state => state.orgs.isOpenUnsavedModal);
  const redirect_URL = useAppSelector(state => state.orgs.org_Redirect_URL);
  const hasTagList = useAppSelector(state => state.orgs.tagList);
  const hasEnumList = useAppSelector(state => state.orgs.enumList);

  const originalOrgDetails = useMemo(() => {
    return {
      name: '',
      activeStatus: 0,
      data: [],
      function: selectedRole?.functionId,
    };  
  }, [selectedRole]);
  
  const [orgDetails, setOrgDetails] = useState<ApiOrganization>({
    name: '',
    activeStatus: 0,
    data: [],
    function: selectedRole?.functionId,
  });

  const [hasOrgDetailsError, setHasOrgDetailsError] = useState<boolean>(false);
  const [isHeaderButtonLoading, setIsHeaderButtonLoading] = useState<boolean>(false);
  const [isHeaderButtonDisabled, setIsHeaderButtonDisabled] = useState<boolean>(true);
  const [showOrgDetailsError, setShowOrgDetailsError] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [isToastOpen, setIsToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'neutral'>('success');
  const [profileIndex, setProfileIndex] = useState<number>(0);
  const [, setValueForSaveBtn] = useState<number>(0);
  const [openDirtyModal, setOpenDirtyModal] = useState<boolean>(false);
  const [validationList, setValidationList] = useState<{[key: string]: string[]}>({});
  const [imageURL, setImageURL] = useState<string | undefined>('');
  const [parentOrg, setParentOrg] = useState<ApiOrganization>();
  const [isOrgDetailsDirty, setIsOrgDetailsDirty] = useState<boolean>(false);
  const [licenseFlags, setLicenseFlags] = useState<IlicenseFlags | undefined>();
  const [haveSeenSettings, setHaveSeenSettings] = useState<boolean>(false);
  const [haveSeenSummary, setHaveSeenSummary] = useState<boolean>(false);
  const [tagList, setTagList] = useState<ApiCustomField[]>(hasTagList);
  const [enumList, setEnumList] = useState<ApiEnumGroup[]>(hasEnumList);

  const orgType = functionStructureList?.find((item) => {
    return item.id === orgDetails?.function;
  })?.name || '';

  const summaryTabData = useMemo(() => {
    const mandatoryOrgDetails = {
      name: messages({ id: 'CERTIFICATION_ORG_NAME' }),
      countryId: messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' }),
    };

    const mandatorySettings = {
      parentOrgName: messages({ id: 'PARENT_ORGANIZATION' }),
      orgTypeId: messages({ id: 'ORGANIZATION_TYPE' }),
    };

    let orgDetailsCompletedFields = 0;
    let orgDetailsMissingFields: string[] = [];
    let settingsCompletedFields = 0;
    let settingsMissingFields: string[] = [];

    const totalFields = Object.keys(mandatoryOrgDetails)?.length + Object.keys(mandatorySettings)?.length;
    const orgTotalFields = Object.keys(mandatoryOrgDetails)?.length;
    const settingsTotalFields = Object.keys(mandatorySettings)?.length;

    const name = orgDetails?.name || '';
    const country = orgDetails?.address?.countryId || null;
    isEmpty(name) ? orgDetailsMissingFields.push(mandatoryOrgDetails?.name) : orgDetailsCompletedFields++;
    !country ? orgDetailsMissingFields.push(mandatoryOrgDetails?.countryId) : orgDetailsCompletedFields++;
    
    const parentOrgName = parentOrg?.name || null;
    const orgTypeId = orgType;
    !parentOrgName ? settingsMissingFields.push(mandatorySettings?.parentOrgName) : settingsCompletedFields++;
    !orgTypeId ? settingsMissingFields.push(mandatorySettings?.orgTypeId) : settingsCompletedFields++;
    
    /** Formats the missing fields into a human-readable list */
    const formatMessage = (fields: string[]) => {
      if (fields?.length === 1) {
        return fields?.[0] + messages({ id: 'MEMBER_MISSING_FIELD_MESSAGE_SINGLE' }); // Return the single element as a string
      } else if (fields?.length > 1) {
        const allButLast = fields.slice(0, -1); // Get all except the last element
        const lastElement = fields[fields?.length - 1]; // Get the last element
        return `${allButLast?.join(', ')} ${messages({ id: 'AND' })} ${lastElement}${messages({ id: 'MEMBER_MISSING_FIELD_MESSAGE_MULTIPLE' })}`;  // Join the rest with commas and add 'and' before the last element
      }
    };
    
    const percentageCompleted = Math.round(((orgDetailsCompletedFields + settingsCompletedFields) / totalFields) * 100);

    return {
      numberOfOrgDetailsMandatoryAll: orgTotalFields || 0,
      numberOfOrgDetailsMandatoryCompleted: orgDetailsCompletedFields || 0,
      missingMandatoryFields_orgDetails: orgDetailsMissingFields || [],
      orgDetailsToastList: formatMessage(orgDetailsMissingFields) ? [{
        message: formatMessage(orgDetailsMissingFields) || '',
        isError: true,
        open: true
      }] : [],
      numberOfSettingsMandatoryAll: settingsTotalFields || 0,
      numberOfSettingsMandatoryCompleted: settingsCompletedFields || 0,
      missingMandatoryFields_settings: settingsMissingFields || [],
      settingsToastList: formatMessage(settingsMissingFields) ? [{
        message: formatMessage(settingsMissingFields) || '',
        isError: true,
        open: true
      }] : [],
      percentageCompleted: percentageCompleted || 0,
    };
  }, [messages, orgDetails?.name, orgDetails?.address?.countryId, parentOrg?.name, orgType]);

  const handleOrgChange = (data: ApiOrganization) => {
    setOrgDetails(data);
    showOrgDetailsError && validateForm(data);
  };

  const handleSaveOrg = async () => {
    const isFormValid = validateForm();
    
    if (!isFormValid) {
      setShowOrgDetailsError(true);
      setToastSeverity('error');
      setIsToastOpen(true);
      setToastMessage(messages({ id: 'MEMBER_MISSING_FIELD_TOAST' }));
      return;
    }
    
    setIsHeaderButtonLoading(true);

    if (orgDetails && !isEqual(orgDetails, originalOrgDetails)) {
      if (!isFormValid) {
        setShowOrgDetailsError(true);
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_EDIT_SAVE_ERROR_MESSAGE' }));
        return;
      }
      try {
        const payload = {
          ...orgDetails,
          function: selectedRole?.functionId,
          relationships: [
            {
              orgAId: -1,
              orgBId: org_id,
              relType: 1,
              id: -1,
            }
          ],
        };
        const response = await dispatch(saveNewOrg(payload));
        
        if (imageURL && imageURL?.length > 0) {
          const payload = {
            itemId: Number(response?.data),
            typeId: ORG_TYPE_ID,
            base64: imageURL,
          };
          await dispatch(saveImage(payload as ApiImageWrapper));
        }
        navigate(ROOT_PATHS.ORGANISATIONS);
        dispatch(setIsOpenUnsavedModal(false));
        dispatch(setIsToastOpen_navFromAddOrganization(true));
        dispatch(setIsDirty_Org_Details(false));
        dispatch(setOrg_Redirect_URL(''));
      } catch (e) {
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'ORG_SAVE_FAIL' }));
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setIsHeaderButtonLoading(false);
      }
    }
    else {
      setIsHeaderButtonLoading(false);
    }
  };

  const validateForm = (data: ApiOrganization | undefined = orgDetails) => {
    let isValid = true;

    /** name validation */
    const name: string[] = [];
    if (!data?.name || data?.name.trim() === '') {
      isValid = false;
      name.push(messages({ id: 'ORGANIZATION_NAME_EMPTY_CAPTION' }));
    }
    if (data?.name && data?.name?.length > STANDARD_TEXT_MAX_LENGTH) {
      isValid = false;
      name.push(messages({ id: 'ORGANIZATION_NAME_MAX_50' }));
    }

    /** notes validation */
    const notes: string[] = [];
    const hasNotes = data?.data?.find((item) =>
      item.key.toUpperCase() ===
        MEMBER_FIELD_NAME_NOTES.toUpperCase()
    );
    if (hasNotes && hasNotes?.value && hasNotes?.value?.length > ORG_NOTES_MAX_LENGTH) {
      isValid = false;
      notes.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_NOTES_PLACEHOLDER' }));
    }

    /** countryId validation */
    const countryId: string[] = [];
    if (!data?.address?.countryId) {
      isValid = false;
      countryId.push(messages({ id: 'MEMBER_COUNTRY_CAPTION' }));
    }

    /** province validation */
    const province: string[] = [];
    if (data?.address?.province && data?.address?.province?.length > STANDARD_TEXT_MAX_LENGTH) {
      isValid = false;
      province.push(messages({ id: 'MEMBER_PROVINCE_MAX_50_CAPTION' }));
    }

    /** province validation */
    const city: string[] = [];
    if (data?.address?.city && data?.address?.city?.length > STANDARD_TEXT_MAX_LENGTH) {
      isValid = false;
      city.push(messages({ id: 'MEMBER_CITY_MAX_50_CAPTION' }));
    }

    /** province validation */
    const postCode: string[] = [];
    if (data?.address?.postCode && data?.address?.postCode?.length > POST_CODE_MAX_LENGTH) {
      isValid = false;
      postCode.push(messages({ id: 'MEMBER_POSTCODE_MAX_20_CAPTION' }));
    }

    /** addressLine fields validation */
    const addressLineFields = ['addressLine1', 'addressLine2', 'addressLine3'];
    const addresslines: {[key: string]: string[]} = {
      addressLine1: [],
      addressLine2: [],
      addressLine3: [],
    };
    for (const field of addressLineFields) {
      if (data?.address && data?.address?.[field as keyof ApiAddress] 
        && String(data?.address?.[field as keyof ApiAddress])?.length > ADDRESS_MAX_LENGTH
      ) {
        isValid = false;
        addresslines[field].push(messages({ id: 'MEMBER_ADDRESS_MAX_20_CAPTION' }));
      }
    }

    setValidationList((prevState) => ({
      ...prevState,
      name,
      notes,
      countryId,
      province,
      city,
      postCode,
      addressLine1: addresslines.addressLine1,
      addressLine2: addresslines.addressLine2,
      addressLine3: addresslines.addressLine3,
    }));
    
    setHasOrgDetailsError(!isValid);

    return isValid;
  };

  const onImageUpdate = (base64?: string) => {
    setImageURL(base64 ?? '');
  };

  const resetOrgDetails = () => {
    setOrgDetails(originalOrgDetails);
  };

  const onTabChange = (value: number) => {
    setSelectedCard(value);
  };

  useEffect(() => {
    // Put all tagList that are not listed in orgData; doing this will include them on save.
    if (tagList && tagList?.length) {
      const childTagList = tagList.filter(({ fieldType }) => fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER);
  
      const includeRemainingList = childTagList
        .map(({ key, id, itemType }) => ({
          key: key || '',
          value: '',
          id,
          itemType,
        }));
  
      setOrgDetails((prevState) => ({
        ...prevState,
        data: includeRemainingList,
      }));
    }
  }, [tagList]);
  
  useEffect(() => {
    const fetchTagList = async () => {
      if (tagList?.length || !selectedRole?.functionId) return;
      try {
        const data = { itemType: [CustomFieldItemType.Organization], functionId: selectedRole?.functionId };
        const fetch = await dispatch(getCustomFieldByTagAndFunction(data));
        if (fetch.data) {
          setTagList(fetch.data);
          dispatch(setOrg_TagList(fetch.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };

    const fetchCustomDropdownOptions = async () => {
      if (hasEnumList?.length) return;
      try {
        const orgEnum = await dispatch(getEnumList());
        if (orgEnum.data) {
          setEnumList(orgEnum.data);
          dispatch(setOrg_EnumList(orgEnum.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetchCustomDropdownOptions();
    fetchTagList();
  }, [dispatch, hasEnumList?.length, selectedRole?.functionId, tagList?.length]);
  
  useEffect(() => {
    const fetchParentOrg = async () => {
      if (!org_id) {
        setParentOrg(undefined); // Handle the case where parentOrgId is undefined
        return;
      }
      const result = await dispatch(getOrganization(org_id));
      setParentOrg(result?.data);
    };

    const fetchLicenses = async () => {
      try {
        const licenseResponse = await dispatch(getLicenseInfo());
        setLicenseFlags(licenseResponse.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
  
    fetchParentOrg();
    fetchLicenses();
  }, [org_id, dispatch]);

  /** Check if org has unsaved changes */
  useEffect(()=>{
    const isOrgDetailsDirtyTemp = !isEqual(orgDetails, originalOrgDetails) || Boolean(imageURL && imageURL !== '');
    setIsOrgDetailsDirty(isOrgDetailsDirtyTemp);
    if (isOrgDetailsDirtyTemp || isOrgDetailsDirtyTemp){
      dispatch(setIsDirty_Org_Details(true));
    } else {
      dispatch(setIsDirty_Org_Details(false));
    }
  },[orgDetails, originalOrgDetails, dispatch, isOrgDetailsDirty, setIsOrgDetailsDirty, imageURL]);

  /** Handle save button disable toggle */
  useEffect(()=>{
    const isHeaderBtnDisabled = !editAccess || !isOrgDetailsDirty;
    setIsHeaderButtonDisabled(isHeaderBtnDisabled);
  },[editAccess, isOrgDetailsDirty]);

  useEffect(() => {
    selectedCard === 1 && setHaveSeenSettings(true);
    selectedCard === 2 && setHaveSeenSummary(true);
  }, [selectedCard]);

  useEffect(() => {
    if (functionStructureList?.length) return;
    const fetch = async () => {
      // if there is no structure list, fetch the list again
      try {
        const structure = await dispatch(getStructure());
        if (structure?.data.length) {
          dispatch(setFunctionStructureList(structure.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetch();
  }, [dispatch, functionStructureList?.length]);

  const mainTabContent = [
    {
      label: messages({ id: 'ORGANIZATION_DETAILS' }),
      children: <OrganizationDetails
        setOrgDetails={handleOrgChange}
        identity={orgDetails}
        editAccess={editAccess}
        showError={showOrgDetailsError}
        validationList={validationList}
        imageURL={imageURL}
        onImageUpdate={onImageUpdate}
        tagList={tagList}
        enumList={enumList}
      />
    },
    {
      label: messages({ id: 'Settings' }),
      children: <Settings
        parentOrg={parentOrg as ApiOrganization}
        editAccess={editAccess}
        deforestActive={licenseFlags?.canDoDeforestCheck}
        orgType={orgType}
      />
    },
  ];

  const summaryTabContent = [
    {
      children:
        <SummaryCardPage
          orgDetails={orgDetails}
          setSelectedCard={setSelectedCard}
          numberOfOrgDetailsMandatoryAll={summaryTabData?.numberOfOrgDetailsMandatoryAll}
          numberOfOrgDetailsMandatoryCompleted={summaryTabData?.numberOfOrgDetailsMandatoryCompleted}
          orgDetailsToastList={summaryTabData?.orgDetailsToastList}
          missingMandatoryFields_orgDetails={summaryTabData?.missingMandatoryFields_orgDetails}
          settings={{
            parentOrg: parentOrg?.name,
            orgType,
          }}
          numberOfSettingsMandatoryAll={summaryTabData?.numberOfSettingsMandatoryAll}
          numberOfSettingsMandatoryCompleted={summaryTabData?.numberOfSettingsMandatoryCompleted}
          settingsToastList={summaryTabData?.settingsToastList}
          missingMandatoryFields_settings={summaryTabData?.missingMandatoryFields_settings}
        />
    }
  ];

  const tabsData: AgTabsDataProps[][] = [
    mainTabContent,
    mainTabContent,
    summaryTabContent
  ];

  const viewAvatarModal = (
    <AgModal
      closeIcon
      open={isOpenViewAvatar}
      setOpen={() => {
        dispatch(setIsOpenViewAvatar(false));
      }}
      title={messages({ id: 'MEMBER_AVATAR_POPUP_TITLE' })}
      body={
        <AgTypography>
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px' }}
            message={orgDetails?.name || ''}
          />
          <AgTypography style={{ marginTop: '15px', textAlign: 'center' }}>
            {imageURL ?
              <img
                src={imageURL}
                alt={messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' })}
                style={{ width: '508px', height: '466px', objectFit: 'contain' }}
              /> :
              <AgAvatar
                src={defaultAvatar} alt={messages({ id: 'AVATAR_ALT' })}
                style={{ width: '508px', height: '466px' }}
              />}
          </AgTypography>
        </AgTypography>
      }
    />
  );

  const profileUnsavedModal = ( // because this modal would redirect in-between tabs, instead of navigating to other pages, we need it separately
    openDirtyModal &&
    <AgModal
      closeIcon
      open={openDirtyModal}
      setOpen={() => {
        dispatch(setIsOpenUnsavedModal(false));
      }}
      title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
      body={
        <AgTypography
          contentStyle={'AP.Paragraph.Large.Regular'}
          style={{ marginTop: '16px', marginBottom: '80px' }}
          message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
        />
      }
      footer={
        <>
          <AgButton
            label={messages({ id: 'CANCEL' })}
            variant='outlined'
            style={{ marginRight: '32px' }}
            onClick={() => {
              setOpenDirtyModal && setOpenDirtyModal(false);
            }}
          />
          <AgButton
            label={messages({ id: 'PROCEED' })}
            onClick={() => {
              dispatch(setIsDirty_Org_Details(false));
              setShowOrgDetailsError(false);
              setOpenDirtyModal && setOpenDirtyModal(false);
              resetOrgDetails && resetOrgDetails();
              setSelectedCard && setSelectedCard(profileIndex ?? 0);
            }}
          />
        </>
      }
      footerSX={{
        boxSizing: 'border-box'
      }}
    />
  );

  const progressBarItems = [
    {
      id: '1',
      title: messages({ id: 'ORGANIZATION_CREATE' }),
      subTitle: messages({ id: 'ORGANIZATION_ENTER_DETAILS' }),
      isCompleted: summaryTabData?.percentageCompleted === 100,
      isActive: selectedCard === 0,
      onClick: () => {
        setValueForSaveBtn(0);
        setSelectedCard(0);
      }
    },
    {
      id: '2',
      title: messages({ id: 'MANAGE_SETTINGS' }),
      subTitle: '',
      isCompleted: haveSeenSettings, // in case no field is mandatory, user has to see the page
      isActive: selectedCard === 1,
      onClick: () => {
        setValueForSaveBtn(1);
        setSelectedCard(1);
      }
    },
    {
      id: '3',
      title: messages({ id: 'MEMBER_SUMMARY' }),
      subTitle: messages({ id: 'MEMBER_SUMMARY_SUB' }),
      isCompleted: summaryTabData?.percentageCompleted === 100 && haveSeenSummary,  // in case no field is mandatory, user has to see the page
      isActive: selectedCard === 2,
      onClick: () => setSelectedCard(2)
    }
  ];

  return (
    
    <AgTypography
      style={{
        display: 'grid',
        padding: '16px 32px',
        gap: '8px'
      }}
    >
      
      <AgProgressBar
        items={progressBarItems}
        percent={summaryTabData?.percentageCompleted ?? 0}
        showBtn={true}
        disableBtn={isHeaderButtonDisabled || summaryTabData?.percentageCompleted !== 100}
        forward={() => {
          if (selectedCard >= 2) {
            setSelectedCard(2);
            return;
          }
          if (selectedCard === 1) {
            setSelectedCard(prevState => prevState + 1);
            return;
          }
          setSelectedCard(prevState => prevState + 1);
        }}
        backward={() => {
          if (selectedCard <= 0) {
            setSelectedCard(0);
            return;
          }
          
          if (selectedCard === 2) {
            setSelectedCard(prevState => prevState - 1);
            return;
          }

          setSelectedCard(prevState => prevState - 1);
        }}
        forwardDisabled={selectedCard >= 2}
        backwardDisabled={selectedCard <= 0}
        btnLabel={messages({ id: 'ORGANIZATION_CREATE_ORG_BTN' })}
        btnClick={handleSaveOrg}
        isAddNewButtonLoading={isHeaderButtonLoading}
        sx={{ maxWidth: '1104px', boxSizing: 'border-box' }}
      />
      <AgTypography
        style={{
          display: 'flex',
          gap: '8px',
          justifyContent: 'start',
          minHeight: '100%'
        }}
      >
        <AgTypography
          style={{
            flex: 1,
            maxWidth: '880px',
          }}
        >
          <AgProfilePage
            headerHasPersonalDetailsError={hasOrgDetailsError && showOrgDetailsError}
            headerTabsData={tabsData || []}
            // headerButtonLabel={messages({ id: 'SAVE' })}
            headerCardInfo={ selectedCard === 2 ? messages({ id: 'ORGANIZATION_SUMMARY_DESCRIPTION' }) : undefined}
            headerButtonDisabled={isHeaderButtonDisabled}
            headerCardTitle={messages({ id: 'CERTIFICATION_ORG_ID' })}
            headerCardName={orgDetails?.id ? '#c' + orgDetails?.id?.toString() : ''}
            headerButtonOnClick={handleSaveOrg}
            isHeaderButtonLoading={isHeaderButtonLoading}
            navButtonOnClick={() => {
            }}
            navHideInfoCard={true}
            navAccountImg={imageURL || defaultAvatar}
            navAccountUserType={orgDetails?.purpose}
            navAccountName={orgDetails?.name || ''}
            navAccountID={orgDetails?.id ? '#c' + orgDetails?.id?.toString() : ''}
            navAccountVillage={orgDetails?.address?.city || ''}
            navAccountEmail={orgDetails?.salesEmail ?? ''}
            navAccountPhone={orgDetails?.phoneNumber ?? ''}
            navInactive={false}
            navMemberProfile={orgDetails?.activeStatus}
            resetPersonalDetailsToOriginal={resetOrgDetails}
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            isDirty={isOrgDetailsDirty}
            openDirtyModal={openDirtyModal}
            setOpenDirtyModal={setOpenDirtyModal}
            profileIndex={profileIndex}
            setProfileIndex={setProfileIndex}
            profileUnsavedModal={profileUnsavedModal}
            viewAvatarModal={viewAvatarModal}
            style={{
              height: '100%'
            }}
            navHideAddButton
            setValueForSaveBtn={setValueForSaveBtn}
            navigationHeaderTitle1={messages({ id: 'ORGANIZATION_PAGE_NAVI_HEADER' })}
            accountProfileCardTitle={messages({ id: 'ORGANIZATION_DETAILS' })}
            accountProfileCardIdLabel={messages({ id: 'CERTIFICATION_ORG_ID' })}
            updatedTabValue={selectedCard === 1 ? 1 : 0}
            showSteps
            stepsLabels={[
              messages({ id: 'ORGANIZATION_CREATE' }),
              messages({ id: 'MANAGE_SETTINGS' }),
            ]}
            fixedContent
            hideOrgDataAndShowSettings
            onTabChange={onTabChange}
          />
        </AgTypography>
        <RightSidebar orgData={parentOrg} totalProfilesStyles={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }} />

        {/* Unsaved changes modal */}
        <AgModal
          closeIcon
          open={isOpenUnsavedModal}
          setOpen={() => {
            dispatch(setIsOpenUnsavedModal(false));
          }}
          title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
          body={
            <AgTypography
              contentStyle={'AP.Paragraph.Large.Regular'}
              style={{ marginTop: '16px', marginBottom: '80px' }}
              message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
            />
          }
          footer={
            <>
              <AgButton
                label={messages({ id: 'CANCEL' })}
                variant='outlined'
                style={{ marginRight: '32px' }}
                onClick={() => {
                  dispatch(setIsOpenUnsavedModal(false));
                }}
              />
              <AgButton
                label={messages({ id: 'PROCEED' })}
                onClick={() => {
                  dispatch(setIsOpenUnsavedModal(false));
                  setShowOrgDetailsError(false);
                  dispatch(setIsDirty_Org_Details(false));
                  navigate(redirect_URL, { replace: true });
                  dispatch(setOrg_Redirect_URL(''));
                }}
              />
            </>
          }
          footerSX={{
            boxSizing: 'border-box'
          }}
        />

        {isToastOpen && <AgToast open={isToastOpen} temporary onIconClick={() => {
          setIsToastOpen(false);
        }} severity={toastSeverity} message={toastMessage} />}

      </AgTypography>
    </AgTypography>
  );
};
 
export default AddOrganization;